import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { apiClient } from '../../framework/api/api.client';
import { useConfirm } from '../../hooks/useConfirm';

interface BrowseParentApplicationsProps {
  open: boolean;
  onClose: () => void;
}

export const BrowseParentApplications: React.FC<BrowseParentApplicationsProps> = ({
  open,
  onClose,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { confirm, ConfirmDialog } = useConfirm();
  
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [importing, setImporting] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadNeoConnections();
    }
  }, [open]);

  const loadNeoConnections = async () => {
    setLoading(true);
    try {
      const { data } = await dataProvider.getList('connection', {
        filter: { connectionProviderKey: 'neo-platform', status: 'configured' },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      setConnections(data);
      
      // Auto-select if only one connection
      if (data.length === 1) {
        setSelectedConnection(data[0].id);
        await discoverApps(data[0].id);
      }
    } catch (error: any) {
      console.error('Failed to load connections:', error);
      notify(`Failed to load connections: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const discoverApps = async (connectionId: string) => {
    if (!connectionId) return;
    
    setDiscovering(true);
    setApplications([]);
    
    try {
      const result = await apiClient.call(`/sync/discover-from-parent?connectionId=${connectionId}`);
      
      if (result.success) {
        setApplications(result.applications || []);
        notify(`Found ${result.applications?.length || 0} applications from parent`, { type: 'success' });
      } else {
        notify('No applications found', { type: 'info' });
      }
    } catch (error: any) {
      console.error('Failed to discover applications:', error);
      notify(`Failed to discover applications: ${error.message}`, { type: 'error' });
    } finally {
      setDiscovering(false);
    }
  };

  const handleImportApp = async (appId: string, appName: string) => {
    if (!selectedConnection) return;
    
    const confirmed = await confirm({
      title: 'Import Application',
      description: `Import "${appName}" from parent platform?`,
      content: (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will create a new application in your environment.
        </Typography>
      ),
      confirmText: 'Import',
      confirmColor: 'primary',
      variant: 'question',
    });
    
    if (!confirmed) {
      return;
    }
    
    setImporting(appId);
    
    try {
      const result = await apiClient.post('/sync/import-from-parent', {
        connectionId: selectedConnection,
        applicationId: appId,
        includeUIAssets: true,
      });
      
      if (result.success) {
        notify(`✅ Successfully imported ${appName}!`, { type: 'success' });
        refresh(); // Refresh application list
        onClose(); // Close dialog
      } else {
        notify('Import failed', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Failed to import application:', error);
      notify(`Failed to import: ${error.message}`, { type: 'error' });
    } finally {
      setImporting(null);
    }
  };

  const handleConnectionChange = async (connectionId: string) => {
    setSelectedConnection(connectionId);
    await discoverApps(connectionId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Browse Parent Platform Applications</Typography>
          {selectedConnection && (
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => discoverApps(selectedConnection)}
              disabled={discovering}
            >
              Refresh
            </Button>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : connections.length === 0 ? (
            <Alert severity="warning">
              <Typography variant="body2">
                No parent Neo platform connections found. Create a connection first in the Connections page.
              </Typography>
            </Alert>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Parent Platform Connection</InputLabel>
              <Select
                value={selectedConnection}
                onChange={(e) => handleConnectionChange(e.target.value)}
                label="Parent Platform Connection"
              >
                {connections.map((conn) => (
                  <MenuItem key={conn.id} value={conn.id}>
                    {conn.name} ({conn.config?.instanceUrl})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {discovering ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Discovering applications from parent platform...
            </Typography>
          </Box>
        ) : applications.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell>UI</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{app.name}</Typography>
                      {app.description && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {app.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{app.key}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">v{app.currentVersion}</Typography>
                        {app.versionLabel && (
                          <Typography variant="caption" color="textSecondary">
                            ({app.versionLabel})
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {app.publishedAt ? new Date(app.publishedAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {app.hasUI ? (
                        <Chip label={app.uiType || 'Yes'} size="small" color="primary" />
                      ) : (
                        <Chip label="No UI" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleImportApp(app.id, app.name)}
                        disabled={importing !== null}
                      >
                        {importing === app.id ? 'Importing...' : 'Import'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : selectedConnection && !discovering ? (
          <Alert severity="info">
            No applications available from parent platform. Make sure applications are published on the parent instance.
          </Alert>
        ) : null}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      
      <ConfirmDialog />
    </Dialog>
  );
};

